import { Link } from "react-router-dom";
import { money, fmtDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Bits";

const SpecialSummary = ({ totals, balances, onPay }) => {
  const payable = balances.filter((b) => b.status === "collecting_initials" && b.balance > 0);
  const payableNow = payable.reduce((s, b) => s + b.balance, 0);
  return (
  <div data-testid="special-balance-summary" className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] px-4 py-3">
    {[["Owed", totals.owed, "text-zinc-200", "special-owed"], ["Approved", totals.paid, "text-emerald-300", "special-paid"], ["Pending", totals.pending, "text-amber-300", "special-pending"]].map(([l, v, c, t]) => (
      <div key={l}>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{l}</div>
        <div data-testid={t} className={`font-mono font-semibold ${c}`}>{money(v)}</div>
      </div>
    ))}
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Special balance</div>
      <div data-testid="special-balance" className={`font-mono text-lg font-bold ${totals.balance > 0 ? "text-pink-400" : "text-emerald-400"}`}>{money(totals.balance)}</div>
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Payable now</div>
      <div data-testid="special-payable-now" className="font-mono font-semibold text-amber-200">{money(payableNow)}</div>
    </div>
    {payable.length > 0 && (
      <div className="ml-auto flex flex-wrap gap-2">
        {payable.map((b) => (
          <button key={b.special_drop_id} type="button" data-testid={`pay-special-balance-button-${b.special_drop_id}`} onClick={() => onPay(b.special_drop_id)} className="rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-semibold text-zinc-950 transition-[transform,background-color] duration-200 hover:bg-amber-300 hover:-translate-y-0.5">
            Pay {b.name} · {money(b.balance)}
          </button>
        ))}
      </div>
    )}
    {totals.balance > payableNow + 0.005 && <p data-testid="special-not-payable-note" className="basis-full text-[11px] text-zinc-500">Special drops open for payment once they're set to Collecting Initials.</p>}
  </div>
  );
};

export default function SpecialClaimsList({ claims, totals, balances = [], onPay }) {
  if (!claims.length) return <EmptyState testId="special-claims-empty" title="No special drop claims yet" hint="Merch, Snapism and Washi Tape claims show up here." />;
  return (
    <div data-testid="joiner-special-claims">
      <p className="text-xs text-zinc-500 mb-3">Special drop claims are billed separately and never added to your regular Balance due. Pay them from Payment Proof once the special drop is Collecting Initials.</p>
      {totals && <SpecialSummary totals={totals} balances={balances} onPay={onPay} />}
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-zinc-900/60">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
            <tr>{["Special Drop", "Item", "Type", "Status", "Qty", "Amount"].map((h, i) => <th key={h} className={`px-3 py-2.5 font-semibold whitespace-nowrap ${i >= 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {claims.map((c) => (
              <tr key={c.id} data-testid={`joiner-special-claim-${c.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                <td className="px-3 py-2.5">
                  <Link to={`/special/${c.special_drop_id}`} className="font-semibold hover:text-amber-200 transition-colors">{c.special_drop_name}</Link>
                  <div className="text-[11px] text-zinc-500">{fmtDate(c.created_at)}</div>
                </td>
                <td className="px-3 py-2.5">{c.item_name}</td>
                <td className="px-3 py-2.5 text-zinc-400 whitespace-nowrap">{c.item_type}</td>
                <td className="px-3 py-2.5">{c.status && <StatusBadge status={c.status} />}</td>
                <td className="px-3 py-2.5 text-right font-mono">{c.qty}</td>
                <td className={`px-3 py-2.5 text-right font-mono ${c.forfeited ? "text-zinc-600" : ""}`}>
                  <span className={c.forfeited ? "line-through" : ""}>{money(c.forfeited ? c.qty * c.price : c.qty * c.price + (c.late_fee || 0))}</span>
                  {c.forfeited && <div data-testid={`special-claim-forfeited-${c.id}`} className="text-[11px] text-red-300">Dropped · unpaid</div>}
                  {!c.forfeited && c.late_fee > 0 && <div data-testid={`special-claim-late-fee-${c.id}`} className="text-[11px] text-red-300">incl. {money(c.late_fee)} late fee</div>}
                  {!c.forfeited && c.fee_waived && <div data-testid={`special-claim-fee-waived-${c.id}`} className="text-[11px] text-emerald-300">Late fee waived</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
